import { useCallback, useMemo, useRef, useState } from 'react'
import { CaretDownIcon, CaretUpIcon, UploadSimpleIcon } from '@phosphor-icons/react'
import { PICTORIAL_DEFAULT_OBJECT } from '../../styleConsts'
import { debounce, cloneDeep } from 'lodash'
import { propExistsOnObject } from '../../utils'
import {
    ScoopButton,
    ScoopColumn,
    ScoopRow,
    ScoopSlider,
    ScoopSwitch,
    ScoopText,
} from '../../../../../../ui'
import styles from './PictorialConfig.module.scss'
import { useModal, MODALS } from '../../../../../../modals'

export const PictorialConfig = ({ config, setConfig, chartState, chartPreferences }) => {
    const selectedRow = useRef(null)
    const [editingRow, setEditingRow] = useState(null)

    // Modal Manager hooks
    const pictorialSelectModal = useModal(MODALS.EXPLORER.PICTORIAL_SELECT)

    const categories = useMemo(() => {
        return chartState.categoryLegendData.map((item) => item.name)
    }, [chartState.categoryLegendData])

    const values = useMemo(() => {
        return chartState.categoryAxisValues
    }, [chartState.categoryAxisValues])

    const setPropValue = (prop, value, categoryIndex, valueIndex) => {
        if (categoryIndex === -1 || valueIndex === -1) return
        const newConfig = { ...config }
        if (!newConfig.styleOverrides?.pictorialBar?.data) {
            newConfig.styleOverrides = {
                ...newConfig.styleOverrides,
                pictorialBar: {
                    ...newConfig.styleOverrides.pictorialBar,
                    data: [],
                },
            }
        }
        if (!newConfig.styleOverrides.pictorialBar.data[categoryIndex])
            newConfig.styleOverrides.pictorialBar.data[categoryIndex] = []
        if (!newConfig.styleOverrides.pictorialBar.data[categoryIndex][valueIndex])
            newConfig.styleOverrides.pictorialBar.data[categoryIndex][valueIndex] = {
                ...PICTORIAL_DEFAULT_OBJECT,
            }
        newConfig.styleOverrides.pictorialBar.data[categoryIndex][valueIndex][prop] = value
        setConfig(newConfig)
    }

    const debouncedFunc = useCallback(
        debounce((prop, value, categoryIndex, valueIndex) => {
            if (categoryIndex === -1 || valueIndex === -1) {
                categories?.forEach((_, c) => {
                    values?.forEach((_, v) => {
                        setPropValue(prop, value, c, v)
                    })
                })
            } else {
                setPropValue(prop, value, categoryIndex, valueIndex)
            }
        }, 200),
        [config]
    )

    const getRootPropValue = (prop) => {
        if (propExistsOnObject(config.styleOverrides.pictorialBar, prop))
            return config.styleOverrides.pictorialBar[prop]
        if (propExistsOnObject(chartPreferences.pictorialBar, prop))
            return chartPreferences.pictorialBar[prop]
        return PICTORIAL_DEFAULT_OBJECT[prop]
    }

    const getPropValue = (prop, categoryIndex, valueIndex) => {
        if (applyToAll) {
            if (propExistsOnObject(config.styleOverrides?.pictorialBar?.data?.[0]?.[0], prop))
                return config.styleOverrides.pictorialBar.data[0][0][prop]
            if (propExistsOnObject(chartPreferences.pictorialBar?.data?.[0]?.[0], prop))
                return chartPreferences.pictorialBar.data[0][0][prop]
            return PICTORIAL_DEFAULT_OBJECT[prop]
        }
        if (
            propExistsOnObject(
                config.styleOverrides?.pictorialBar?.data?.[categoryIndex]?.[valueIndex],
                prop
            )
        )
            return config.styleOverrides.pictorialBar.data[categoryIndex][valueIndex][prop]
        if (
            propExistsOnObject(
                chartPreferences.pictorialBar?.data?.[categoryIndex]?.[valueIndex],
                prop
            )
        )
            return chartPreferences.pictorialBar.data[categoryIndex][valueIndex][prop]
        return PICTORIAL_DEFAULT_OBJECT[prop]
    }

    const handleDebouncedSetter = (prop, value, setter, valueIndex, categoryIndex) => {
        debouncedFunc(prop, value, categoryIndex, valueIndex)
        setter(value)
    }

    const handleSelectImage = (image) => {
        const imageValue = `image://${image}`
        if (applyToAll) {
            categories?.forEach((_, categoryIndex) => {
                values?.forEach((_, valueIndex) => {
                    setPropValue('symbol', imageValue, categoryIndex, valueIndex)
                })
            })
        } else {
            const { category, value } = selectedRow.current
            const valueIndex = values?.indexOf(value)
            const categoryIndex = categories?.indexOf(category)
            setPropValue('symbol', imageValue, categoryIndex, valueIndex)
        }
    }

    const handleOpenSelect = (e, category, value) => {
        e.stopPropagation()
        selectedRow.current = { category, value }
        pictorialSelectModal.show({
            onConfirm: handleSelectImage,
        })
    }

    const setRootPropValue = (prop, value) => {
        const newConfig = cloneDeep(config)
        newConfig.styleOverrides.pictorialBar[prop] = value
        setConfig(newConfig)
    }

    const shouldExpand = (category, value) => {
        if (!editingRow) return false
        return editingRow.value === value && editingRow.category === category
    }

    const getSelectedImage = (category, value) => {
        const categoryData = config.styleOverrides.pictorialBar?.data?.[category]
        if (!categoryData) return null
        const valueData = categoryData?.[value]
        if (!valueData) return null
        return valueData['symbol'] || null
    }

    const handlePropChange = (prop, value, categoryIndex, valueIndex) => {
        if (applyToAll) {
            categories?.forEach((_, categoryIndex) => {
                values?.forEach((_, valueIndex) => {
                    setPropValue(prop, value, categoryIndex, valueIndex)
                })
            })
        } else {
            setPropValue(prop, value, categoryIndex, valueIndex)
        }
    }

    const showAsBar = getRootPropValue('showAsBar')
    const applyToAll = getRootPropValue('applyAll')

    return (
        <ScoopColumn className={styles.container}>
            <ScoopRow className={styles.switchRow}>
                <ScoopText className={styles.label}>Show as bar</ScoopText>
                <ScoopSwitch
                    checked={showAsBar}
                    onChange={() => setRootPropValue('showAsBar', !showAsBar)}
                />
            </ScoopRow>
            <ScoopRow className={styles.switchRowNoMargin}>
                <ScoopText className={styles.label}>Apply to all</ScoopText>
                <ScoopSwitch
                    checked={applyToAll}
                    onChange={() => setRootPropValue('applyAll', !applyToAll)}
                />
            </ScoopRow>
            {!applyToAll ? (
                categories?.map((category, categoryIndex) => (
                    <div className={styles.categorySection} key={categoryIndex}>
                        <ScoopText className={styles.label}>{category}</ScoopText>
                        {values?.map((value, valueIndex) => (
                            <div key={valueIndex}>
                                <PictorialRow
                                    category={category}
                                    value={value}
                                    setEditingRow={setEditingRow}
                                    shouldExpand={shouldExpand}
                                    categories={categories}
                                    values={values}
                                    getSelectedImage={getSelectedImage}
                                    handleOpenSelect={handleOpenSelect}
                                />
                                <div
                                    className={`${styles.collapseWrapper} ${shouldExpand(category, value) ? styles.collapseOpen : ''}`}
                                >
                                    <PictorialStyles
                                        categoryIndex={categoryIndex}
                                        valueIndex={valueIndex}
                                        initialRotation={getPropValue(
                                            'symbolRotate',
                                            categoryIndex,
                                            valueIndex
                                        )}
                                        initialSymbolSize={getPropValue(
                                            'symbolSize',
                                            categoryIndex,
                                            valueIndex
                                        )}
                                        repeat={getPropValue(
                                            'symbolRepeat',
                                            categoryIndex,
                                            valueIndex
                                        )}
                                        clip={getPropValue('symbolClip', categoryIndex, valueIndex)}
                                        handlePropChange={handlePropChange}
                                        handleDebouncedSetter={handleDebouncedSetter}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                ))
            ) : (
                <div className={styles.categorySection}>
                    <PictorialRow
                        category="applyAll"
                        value="applyAll"
                        setEditingRow={setEditingRow}
                        shouldExpand={shouldExpand}
                        categories={categories}
                        values={values}
                        getSelectedImage={getSelectedImage}
                        handleOpenSelect={handleOpenSelect}
                    />
                    <PictorialStyles
                        categoryIndex={-1}
                        valueIndex={-1}
                        initialRotation={getPropValue('symbolRotate', 0, 0)}
                        initialSymbolSize={getPropValue('symbolSize', 0, 0)}
                        repeat={getPropValue('symbolRepeat', 0, 0)}
                        clip={getPropValue('symbolClip', 0, 0)}
                        handlePropChange={handlePropChange}
                        handleDebouncedSetter={handleDebouncedSetter}
                    />
                </div>
            )}
        </ScoopColumn>
    )
}

const PictorialRow = ({
    category,
    value,
    setEditingRow,
    shouldExpand,
    categories,
    values,
    getSelectedImage,
    handleOpenSelect,
}) => {
    const applyAll = category === 'applyAll' && value === 'applyAll'
    const valueIndex = values?.indexOf(value)
    const categoryIndex = categories?.indexOf(category)

    const isExpanded = shouldExpand(category, value)

    const handleIconClick = () => {
        if (applyAll) return
        setEditingRow(isExpanded ? null : { category, value })
    }

    const imageSelected = applyAll
        ? getSelectedImage(0, 0)
        : getSelectedImage(categoryIndex, valueIndex)?.split('image://')[1]

    const imageName = imageSelected?.split('/')[imageSelected?.split('/').length - 1]

    return (
        <ScoopRow className={styles.rowContainer} onClick={handleIconClick}>
            <ScoopColumn>
                <ScoopText className={styles.label}>{applyAll ? 'Select Image' : value}</ScoopText>
                <ScoopText className={styles.imageName}>{imageName}</ScoopText>
            </ScoopColumn>
            <ScoopRow className={styles.rowRight}>
                <ScoopButton
                    variant="ghost"
                    size="xs"
                    onClick={(e) => handleOpenSelect(e, category, value)}
                    className={styles.uploadButton}
                >
                    <UploadSimpleIcon
                        size={20}
                        className={imageSelected ? styles.uploadIconActive : styles.uploadIcon}
                    />
                </ScoopButton>
                {applyAll ? null : !isExpanded ? (
                    <CaretUpIcon size={20} className={styles.expandIcon} />
                ) : (
                    <CaretDownIcon size={20} className={styles.expandIcon} />
                )}
            </ScoopRow>
        </ScoopRow>
    )
}

const PictorialStyles = ({
    categoryIndex,
    valueIndex,
    initialRotation,
    initialSymbolSize,
    repeat,
    clip,
    handlePropChange,
    handleDebouncedSetter,
}) => {
    const [rotation, setRotation] = useState(initialRotation)
    const [symbolSize, setSymbolSize] = useState(initialSymbolSize)

    return (
        <ScoopColumn className={styles.stylesContainer}>
            <ScoopRow className={styles.stylesRow}>
                <ScoopText className={styles.label}>Repeat</ScoopText>
                <ScoopSwitch
                    checked={repeat}
                    onChange={() =>
                        handlePropChange('symbolRepeat', !repeat, categoryIndex, valueIndex)
                    }
                />
            </ScoopRow>
            <ScoopRow className={styles.stylesRow}>
                <ScoopText className={styles.label}>Clip image</ScoopText>
                <ScoopSwitch
                    checked={clip}
                    onChange={() =>
                        handlePropChange('symbolClip', !clip, categoryIndex, valueIndex)
                    }
                />
            </ScoopRow>
            <ScoopSlider
                label={`Rotate: ${rotation}°`}
                value={rotation}
                onChange={(v) =>
                    handleDebouncedSetter('symbolRotate', v, setRotation, valueIndex, categoryIndex)
                }
                min={0}
                max={360}
                step={1}
            />
            <ScoopSlider
                label={`Width: ${symbolSize[0]}`}
                value={parseInt(symbolSize[0].split('%')[0])}
                onChange={(v) =>
                    handleDebouncedSetter(
                        'symbolSize',
                        [v + '%', symbolSize[1]],
                        setSymbolSize,
                        valueIndex,
                        categoryIndex
                    )
                }
                min={0}
                max={100}
                step={1}
            />
            <ScoopSlider
                label={`Height: ${symbolSize[1]}`}
                value={parseInt(symbolSize[1].split('%')[0])}
                onChange={(v) =>
                    handleDebouncedSetter(
                        'symbolSize',
                        [symbolSize[0], v + '%'],
                        setSymbolSize,
                        valueIndex,
                        categoryIndex
                    )
                }
                min={0}
                max={100}
                step={1}
            />
        </ScoopColumn>
    )
}
